import { getRevAllNodes } from "../../redux/procedureRevisionSlice";
import { setTaskReference as setTaskReferenceAction, updateTask, deleteTask } from "../../redux/taskSlice";
import DeleteConfirmation from "../../common/Confirmations/DeleteConfirmation";

/**
 * Returns true if the given status is a terminal task status (COMPLETED or CANCELLED).
 * @param {string} status
 * @returns {boolean}
 */
export const isTerminalTaskStatus = (status) =>
    status === "COMPLETED" || status === "CANCELLED";

export const setTaskReference = (dispatch, reference, reference_id, task_tree_node_id_ref, procedure_ltree_ref,is_revision=false) => {
    dispatch(setTaskReferenceAction({
        task_reference: reference,
        task_reference_id: reference_id,
        task_tree_node_id_ref: task_tree_node_id_ref,
        procedure_ltree_ref: procedure_ltree_ref,
        is_revision:is_revision
    }));
}

/**
 * Check if the user can add tasks on the current screen
 * based on the current URL pathname
 * @returns {boolean} - true if user can add tasks, false otherwise
 */
export const canAddTaskOnCurrentScreen = () => {
    const pathname = window.location.pathname;

    // List of URL patterns where users can add tasks
    const allowedUrlPatterns = [
        '/revision-submission/',
        '/approve-revision/',
        '/release-revision/',
        '/released-revision/',
        '/rejected-revision/',
        '/archived-revision/',
        '/view-revision/',
        '/view-section/',
        '/edit-section/',
        '/view-step/',
        '/edit-step/',
        '/view-substep/',
        '/edit-substep/',
        '/view-childnode/',
        '/edit-attachment/',
        '/edit-canvas-new/',
        '/edit-rich-text/',
        '/edit-video/',
        '/edit-form/',
    ];

    // Check if current pathname includes any of the allowed patterns
    return allowedUrlPatterns.some(pattern => pathname.includes(pattern));
}

/**
 * Check if the user is on a revision page
 * @returns {boolean} - true if user is on a revision page, false otherwise
 */
export const isRevisionPage = () => {
    const pathname = window.location.pathname;
    const revisionPagePatterns = [
        '/revision-submission/',
        '/approve-revision/',
        '/release-revision/',
        '/released-revision/',
        '/rejected-revision/',
        '/archived-revision/',
    ];
    return revisionPagePatterns.some(pattern => pathname.includes(pattern));
}

/**
 * Formats a date string by extracting the date part from an ISO date string
 * @param {string} dateString - ISO date string (e.g., "2024-01-15T10:30:00")
 * @returns {string} - Formatted date string (e.g., "2024-01-15") or empty string if invalid
 */
export const formatDueDate = (dateString) => {
    if (!dateString) return "";
    return dateString.split("T")[0];
}

/**
 * Returns the full name of a user from the users array by user ID
 * @param {string|number} userId - The user ID to look up
 * @param {Array} users - Array of user objects from the users slice state
 * @returns {string} - Full name (first_name + last_name) or empty string if user not found
 */
export const getUserFullName = (userId, users) => {
    if (!userId || !users || !Array.isArray(users)) return "";
    const user = users.find(u => u.id === userId || u.user_id === userId);
    if (!user) return "";
    return `${user.first_name || ""} ${user.last_name || ""}`.trim();
}

/**
 * Renders user name with conditional highlighting for disabled users
 * @param {Object} user - User object containing first_name, last_name, and status
 * @returns {JSX.Element|string} - Rendered user name element or string
 */
export const renderUser = (user) => {
    if (!user) return "";
    const name = user.first_name && user.last_name
        ? `${user.first_name} ${user.last_name}`
        : user.first_name || user.last_name || "";

    const isDisabled = user.status && String(user.status).toLowerCase() === 'disabled';

    if (isDisabled) {
        return <span style={{ color: "red" }}>{name} (disabled)</span>;
    }
    return name;
};


export const referenceRenderer = (taskinfo) => {
    const { procedure_tree, part_number, reference_details } = taskinfo;
    let reference = "";
    switch (procedure_tree.type) {
        case "procedure revision":
            reference = `Rev ${reference_details?.rev} , ${part_number}`;
            break;
        case "section":
            reference = `Section ${part_number}`;
            break;
        case "step":
            reference = `Step ${part_number}`;
            break;
        case "substep":
            reference = `Substep ${part_number}`;
            break;
        case "rich-text":
            reference = `Rich Text ${part_number}`;
            break;
        case "video":
            reference = `Video ${part_number}`;
            break;
        case "attachment":
            reference = `Attachment ${part_number}`;
            break;
        case "canva":
            reference = `Canvas ${part_number}`;
            break;
        case "form":
            reference = `Form ${part_number}`;
            break;
        default:
            reference = ``;
    }
    return reference;
}

/**
 * Redirects to the reference node based on task information
 * @param {Object} taskinfo - Task information object
 * @param {Function} dispatch - Redux dispatch function
 * @returns {Promise<string>} - URL path to navigate to
 */
export const redirectToReferenceNode = async (taskinfo, dispatch) => {
    const { procedure_tree, part_number, reference_details } = taskinfo;
    const { type, ltree_path } = procedure_tree;

    // Parse ltree_path: partId.revisionId.sectionId.stepId.substepId...
    const ref = ltree_path.replaceAll("_", "-").split(".");
    const [partId, revisionId] = ref;

    // Validate required path segments
    if (!partId || !revisionId) {
        return "/not-found";
    }

    const procedureTreeRef = reference_details?.procedure_tree_ref;
    const revision = procedure_tree?.version?.version_content?.rev || "BB";

    // Helper function to fetch and cache nodes data
    const fetchNodesData = async (is_deleted) => {
        const payload = {
            partId,
            revisionId,
            otherParams: `section_id_ref=${ref[2]}`,
            is_deleted: is_deleted
        };
        const response = await dispatch(getRevAllNodes(payload));
        return response.payload;
    };

    // Helper function to find step by step_id_ref
    const findStep = (steps, stepIdRef) => {
        return steps?.find(step => step.step_id_ref === stepIdRef);
    };

    // Helper function to find substep by substep_id_ref
    const findSubstep = (substeps, substepIdRef) => {
        return substeps?.find(substep => substep.substep_id_ref === substepIdRef);
    };

    let url = "";

    switch (type) {
        case "procedure revision":
            url = `revision/${partId}/${revisionId}`;
            break;

        case "section":
            url = `view-section/${part_number}/${revision}/${partId}/${revisionId}/${procedureTreeRef}`;
            break;

        case "step": {
            const data = await fetchNodesData(procedure_tree.is_deleted);
            const section = data?.[0];
            if (!section) return "/not-found";

            url = `view-step/${part_number}/${revision}/${partId}/${revisionId}/${section.tree_node_id}/${procedureTreeRef}`;
            break;
        }

        case "substep": {
            const data = await fetchNodesData(procedure_tree.is_deleted);
            const section = data?.[0];
            if (!section) return "/not-found";

            const step = findStep(section.steps, ref[3]);
            if (!step) return "/not-found";

            url = `view-substep/${part_number}/${revision}/${partId}/${revisionId}/${section.tree_node_id}/${step.tree_node_id}/${procedureTreeRef}`;
            break;
        }

        case "rich-text":
        case "video":
        case "attachment":
        case "canva":
        case "form": {
            const data = await fetchNodesData(procedure_tree.is_deleted);
            const section = data?.[0];
            if (!section) return "/not-found";

            const step = findStep(section.steps, ref[3]);
            if (!step) return "/not-found";

            // Check if it's a child of substep (ref.length === 6)
            const isSubstepChild = ref.length === 6;
            let substepIdRef = "";

            if (isSubstepChild) {
                const substep = findSubstep(step.substeps, ref[4]);
                if (substep) {
                    substepIdRef = `?substep_id_ref=${substep.tree_node_id}${procedure_tree.is_deleted ? "&is_deleted=true" : ""}`;
                }
            }

            url = `view-childnode/${part_number}/${revision}/${partId}/${revisionId}/${section.tree_node_id}/${step.tree_node_id}/${procedureTreeRef}${substepIdRef}`;
            break;
        }

        default:
            url = "not-found";
    }

    if (procedure_tree.is_deleted) {
        if (url.includes("is_deleted")) {
            return `/${url}`;
        } else {
            return `/${url}?is_deleted=true`;
        }
    }
    return `/${url}`;
}

/**
 * Validates task information before saving or updating
 * @param {Object} curtskinfo - Task information object to validate
 * @param {Function} snackbar - Function to show snackbar messages
 * @returns {boolean} - true if valid, false otherwise
 */
export const validateTaskInfo = (curtskinfo, snackbar) => {
    if (curtskinfo === null) {
        curtskinfo = {};
    }
    if (!curtskinfo.task || curtskinfo.task.trim() === "") {
        snackbar("Please enter task!", "error");
        return false;
    } else if (!curtskinfo.status || curtskinfo.status.trim() === "") {
        snackbar("Please select status!", "error");
        return false;
    } else if (!curtskinfo.assign_to || curtskinfo.assign_to.trim() === "") {
        snackbar("Please select assigned to!", "error");
        return false;
    } else if (!curtskinfo.owned_by || curtskinfo.owned_by.trim() === "") {
        snackbar("Please select owned by!", "error");
        return false;
    }

    // Default to 0 if missing
    // if (curtskinfo.remind === undefined || curtskinfo.remind === null || curtskinfo.remind === "") {
    //     curtskinfo.remind = 0;
    // }
    // if (curtskinfo.escalate === undefined || curtskinfo.escalate === null || curtskinfo.escalate === "") {
    //     curtskinfo.escalate = 0;
    // }
    if(curtskinfo.due?.length === 0){
        curtskinfo.due = null;
    }
    if(Number(curtskinfo.remind) < 0 || (isNaN(Number(curtskinfo.remind)))){
        snackbar("Progress must be a non-negative number!", "error");
        return false;
    }
    if (Number(curtskinfo.remind) < 0 || (isNaN(Number(curtskinfo.remind)))) {
        snackbar("Reminder must be a non-negative number!", "error");
        return false;
    } else if (Number(curtskinfo.escalate) < 0 || (isNaN(Number(curtskinfo.escalate)))) {
        snackbar("Escalate must be a non-negative number!", "error");
        return false;
    } else {
        return true;
    }
};

/**
 * Reusable function to update a task with permission checks and validation
 * @param {Object} params - Parameters object
 * @param {Object} params.editTask - Current task data being edited
 * @param {Object} params.previusTaskInfo - Previous task data for comparison
 * @param {boolean} params.reassign_persmision - Whether user has reassign permission
 * @param {Array} params.users - Array of users for name lookup
 * @param {Function} params.dispatch - Redux dispatch function
 * @param {Function} params.snackbar - Function to show snackbar messages
 * @param {Function} params.onSuccess - Callback function to call after successful update (e.g., refresh tasks)
 * @param {Function} params.setEditTask - Function to reset edit state (setEditTask(null))
 * @returns {Promise<void>}
 */
export const handleUpdateTask = async ({
    editTask,
    previusTaskInfo,
    reassign_persmision,
    users,
    dispatch,
    snackbar,
    onSuccess,
    setEditTask
}) => {
    // Validate task info
    if (!validateTaskInfo(editTask, snackbar)) return;

    // Check if current user is the owner or assignee
    const currentUserId = localStorage.getItem("user_id");
    const isOwner = editTask.owned_by?.toString() === currentUserId?.toString() || reassign_persmision;
    const isAssignee = editTask.assign_to?.toString() === currentUserId?.toString();

    // Ensure owned_by cannot be changed unless user has reassign permission
    const originalOwnedBy = previusTaskInfo?.owned_by;
    const taskData = {
        ...editTask,
        reference: previusTaskInfo.encoded_reference,
        owned_by: reassign_persmision ? editTask.owned_by : (originalOwnedBy || editTask.owned_by) // Only reassign-permitted users can change owner
    };

    // If assignee (but not owner), only allow editing Note, Status, and Progress
    if (isAssignee && !isOwner) {
        // Preserve original values for fields assignee cannot edit
        taskData.tags = previusTaskInfo?.tags || editTask.tags;
        taskData.task = previusTaskInfo?.task || editTask.task;
        taskData.due = previusTaskInfo?.due || editTask.due;
        taskData.assign_to = previusTaskInfo?.assign_to || editTask.assign_to;
        taskData.is_pinned = previusTaskInfo?.is_pinned !== undefined ? previusTaskInfo.is_pinned : false;

        // Validate status - assignee can only set to: OPEN, PENDING, WAITING, IN PROGRESS
        const allowedStatuses = ["OPEN", "PENDING", "WAITING", "IN PROGRESS"];
        if (editTask.status && !allowedStatuses.includes(editTask.status)) {
            snackbar("Assignee can only set status to: OPEN, PENDING, WAITING, or IN PROGRESS.", "error");
            return;
        }
    }
    // Th
    if(taskData.due?.length ===0){
        taskData.due = null;
    }
    // Dispatch update task action
    await dispatch(updateTask({
        task_id: editTask.task_id,
        data: taskData,
        newTaskInfo: {
            ...editTask,
            is_pinned: editTask.is_pinned !== undefined ? editTask.is_pinned : false,
            created_on: formatDueDate(editTask.created_on),
            due: formatDueDate(editTask.due),
            assigned_to: getUserFullName(editTask.assign_to, users),
            owned_by: getUserFullName(editTask.owned_by, users),
        },
        previusTaskInfo: {
            ...previusTaskInfo,
            is_pinned: previusTaskInfo.is_pinned !== undefined ? previusTaskInfo.is_pinned : false,
            created_on: formatDueDate(previusTaskInfo.created_on),
            due: formatDueDate(previusTaskInfo.due),
            assigned_to: getUserFullName(previusTaskInfo.assign_to, users),
            owned_by: getUserFullName(previusTaskInfo.owned_by, users),
        }
    })).then(async (resp) => {
        if (resp.payload.success === false) {
            snackbar(resp.payload.error, "error");
            return;
        } else {
            snackbar(resp.payload.message, "success");
            // Call the onSuccess callback to refresh tasks
            if (onSuccess) {
                await onSuccess();
            }
            // Reset edit state
            if (setEditTask) {
                setEditTask(null);
            }
        }
    });
};

/**
 * Reusable function to delete a task with confirmation
 * @param {Object} params - Parameters object
 * @param {string} params.task_id - Task ID to delete
 * @param {string} params.task_name - Task name for display
 * @param {Function} params.dispatch - Redux dispatch function
 * @param {Function} params.snackbar - Function to show snackbar messages
 * @param {Function} params.onSuccess - Callback function to call after successful deletion (e.g., refresh tasks)
 * @param {Function} params.setEditTask - Optional function to reset edit state (setEditTask(null))
 * @returns {Promise<void>}
 */
export const handleDeleteTask = async ({
    task_id,
    task_name,
    dispatch,
    snackbar,
    onSuccess,
    setEditTask
}) => {
    DeleteConfirmation(async () => {
        await dispatch(deleteTask({ task_id, task_name })).then(async (resp) => {
            if (resp.payload.success === false) {
                snackbar(resp.payload.error, "error");
                return;
            } else {
                snackbar(resp.payload.message, "success");
                // Call the onSuccess callback to refresh tasks
                if (onSuccess) {
                    await onSuccess();
                }
                // Reset edit state if provided
                if (setEditTask) {
                    setEditTask(null);
                }
            }
        });
    }, "This task will be permanently deleted!", "Are you sure?");
};